/**
 * DealRooms 处理房间创建、排序、名称显示 的业务定制逻辑
 * 创建房间思路：
 * 1. 在“ScrollBar”中，注入“DealRooms”单例，处理“房间”数据
 * 2. 在通过 WebIM.conn.getJoinedServers 获取房间列表时，调用“DealRooms” update方法，赋值（问题：华信这个API是分页返回数据，若云渲染房间不在当前页，则找不到房间；方案：在“userInfo”接口返回默认创建房间、云渲染房间）
 * 3. update方法方法内，判断“主人”是否有个人默认房间、是否有云渲染房间，没有则创建房间，有则执行对应业务逻辑（eg：跳转）
 *
 * 修改房间思路：
 * 1. 在房间名称被修改前，在ext中添加“已修改”标记
 * 2. 根据标记，判断房间名字显示 “我的房间”，‘${昵称}的房间’，‘${改名后的房间}’
 *
 * 云渲染跳转打开改图思路：
 * 1. 判断当前房间是否是云渲染房间，并从云渲染跳转过来，通过路由将来源、img参数给到AiOperaCp 组件
 * 2. 根据参数，打开“AI改图”弹窗
 *
 * 优化：
 * 1. 减少循环次数，目前体量小，后续优化
 * 2. 自动创建默认房间、云渲染房间逻辑可以移交给后端，创建后在userinfo中返回房间信息（可以减少请求，但是房间创建逻辑分散在2端，后续不好维护）
 */

import _ from "lodash"
import WebIM from "@/utils/WebIM"
import {getServerChannelDetail, postCircleServer} from "@/api";

/**
 * _roomCreatedForDef、_roomCreatedForXuanran 用数组的结构，是因为默认房间、云渲染房间不唯一时。前端打印报错日志
 * 编码约定：`_${方法名|变量名}`，表示私有方法、私有变量，反之则为公由方法、公有变量
 * 思路：通过ext、joinedServers, user_id 判断“主人”是否有默认房间、云渲染房间、默认房间是否改名，处理相应业务
 * _ownerInfo数据结构：
 * {
 *     from: "xuanran",
 *     extra: "",
 *     hasRoomXuanran: false,
 *     hasRoomDefault: false,
 *     isRenamed: false
 * }
 */
class DealRooms {
    _ownerInfo = {}; // "房主"信息
    _redirectServerId = 0; // 新创建“云渲染”房间Id
    _redirectChannelId = 0; // 新创建“云渲染”房间下改图区Id
    _rooms = []; // 用户加入房间的拷贝
    _roomCreatedForDef = []; // 后台创建的默认房间，房间唯一, 用数组的结构，是因为默认房间、云渲染房间不唯一时, 前端打印错误日志
    _roomCreatedForXuanran = []; // 后台创建的云渲染专属房间，房间唯一
    _roomForOwner = []; // 自己创建的房间
    isDebug = false;

    /**
     * 日志
     */
    _log(log) {
        if(this.isDebug) console.log(log)
    }

    /**
     * 获取url参数
     * @returns {{}}
     */
    _formattedUrl() {
        const url = window.location.search.replace(/&amp;/g, '&');
        let formattedParams = {};
        let params = []
        if(url){
            params  =  url.split("?")[1].split("&");
        }
        for (let i = 0, len = params.length; i < len; i++) {
            formattedParams[params[i].split("=")[0]] = params[i].split("=")[1];
        }
        return formattedParams;
    }

    /**
     * 创建房间，查询房间详情
     * 注意： custom 为业务拓展字段，在 WebIM.conn.getJoinedServers 中 返回为ext 字段
     * @param name 房间名
     * @param description 描述
     * @param ext 业务拓展字段，与custom 是同一个字段
     * @param create_type 创建类型 1-系统创建（公开房间） 2-云渲染
     */
    async _creatRoom(name, description, ext, create_type){
        const res = await postCircleServer({
            name, 
            description, 
            custom: ext,
            type: "0",
            create_type,
            icon_url: 'https://3d66-imgo.oss-cn-hangzhou.aliyuncs.com/ai/reference_image/202504/179175740_1744880540866.png', // 默认房间logo
            background_url: 'https://3d66-imgo.oss-cn-hangzhou.aliyuncs.com/ai/reference_image/202504/179175740_1744880360848.png' // 默认房间背景图
        })
        if (res.status === 200) {
            return await this._getRoomDetail(res.data.server_id)
        } else {
            console.error(`dealRooms._creatRoom方法异常${res.status}`);
            return false;
        }
    }

    /**
     * 调用华信API 获取房间信息
     */
    _getRoomDetail(serverId) {
        return new Promise((resolve, reject) => {
            WebIM.conn.getServerDetail({serverId: serverId}).then(res => {
                try {
                    resolve(res)
                }catch(err) {
                    reject(`dealRooms._getRoomDetail方法异常${err}`);
                }
            }).catch((e) => {
                console.error(`获取社区信息失败:${e}`);
            });
        })
    }

    async _getServerChannelDetail(serverId) {

        const res = await getServerChannelDetail({ server_id: serverId})
        let targetChannel_id
        if (res.status === 200) {
            res.data.forEach((item) => {
                if (item.channels && item.channels.length > 0) {
                    item.channels.forEach((channelItem) => {
                        if (channelItem.name.indexOf('改图') >= 0) {
                            targetChannel_id = channelItem.channel_id;
                        }
                    })
                }
            })
            return targetChannel_id
        }
    }

    /**
     * 算法： 将数组中指定serverId对象放到队首
     */
    _moveToFrontById(arr, targetId) {
        const index = arr.findIndex((item) => item.id === targetId);
        if(index !== -1) {
            const [target] = arr.splice(index, 1);
            arr.unshift(target);
        }
        return arr;
    }

    /**
     * 算法： 将数组中serverId重复的对象删除，只保留最小的索引的对象
     */
    _removeDuplicates(arr) {
        const seen = new Set();
        return arr.filter((item) => {
            if(seen.has(item.id)) {
                return false;
            }
            seen.add(item.id);
            return true;
        })
    }

    /**
     * 解散社区
     * 用法：用于开发时，清除服务器
     */
    destroyOwnerRoom(joinedServers, userInfo){

        if(joinedServers?.length > 0 && userInfo.user_id){
            for(let i = 0; i < joinedServers.length; i++){
                const serverId = joinedServers[i].id;
                const isSelfRoom = joinedServers[i].owner === userInfo.user_id+"";

                if(isSelfRoom){
                    WebIM.conn.destroyServer({serverId}).then(res => {
                        this._log(res)
                    })
                }
            }
        }
    }

    /**
     * 初始化房间列表数据
     * @param joinedServers 用户加入的房间
     * @param userInfo user_id 用户唯一Id avatarurl 用户头像 nickname 用户昵称 default_server 后台创建房间
     * @param pageType 更新类型 1: 首页 2：最后一页
     * @param serverId url 中的房间Id
     * 用法：
     */
    async update(joinedServers, userInfo, pageType, serverId) {

        const {user_id, nickname, default_server} = userInfo;

        // 1. 用户“加入的个人房间”列表
        this._rooms = _.cloneDeep(joinedServers);

        // 2. 主人来源渠道
        const formattedUrl = this._formattedUrl()
        if(formattedUrl && formattedUrl.from && formattedUrl.img){
            this._ownerInfo = {
                user_id,
                from: this._formattedUrl().from, // xuanran 溜云库云渲染； “” 其他
                extra: JSON.stringify({
                    img: this._formattedUrl().img
                }),
            }
        }

        // 3. 房间分类，根据房间的业务标签（ext）分类（type: 1后台创建默认房间; 2后台创建云渲染房间; undefined其他房间）
        for (let i = 0, len = this._rooms.length; i < len; i++) {
            let ext;
            try {
                ext = JSON.parse(this._rooms[i].ext)
            }catch(err) {
                console.error(`ext不是json字符串:${err}`)
            }

            if(ext?.type){
                const {type} = ext;
                if (Number(type) === 1) {
                    this._roomCreatedForDef.push(this._rooms[i]);
                }else if (Number(type) === 2) {
                    this._roomCreatedForXuanran.push(this._rooms[i]);
                }
            }

            const isSelfRoom = Number(this._rooms[i].owner) === Number(user_id);
            if(isSelfRoom) {
                this._roomForOwner.push(this._rooms[i]);
            }
        }

        /**
         * 4. 房间创建
         * 判断“主人”是否有个人默认房间、是否有云渲染房间、房间是否改名；
         * 没有则创建房间；
         * 有默认房间且改名，则显示改名，无则按业务逻辑显示房间名；
         * 有云渲染房间，则拿到房间Id跳转；
         */
        if(pageType === 1) {// 首页
            // 4-1. 创建默认房间 判断没有默认房间; 房间名为`${昵称}的房间`， 房间类型为ext:"{type: 1}"
            if(!default_server?.public){
                const res = await this._creatRoom(`${nickname}的房间`, `默认创建房间`, `${JSON.stringify({type: 1})}`, 1)
                if(res){
                    this._log(`后台创建“默认房间”成功${JSON.stringify(res)}`)
                    this._ownerInfo.hasRoomDefault = true;

                    // 将房间插入到队首
                    this._rooms.unshift(res.data);
                    this._roomCreatedForDef.push(res.data)
                    this._roomForOwner.push(res.data)
                }
            }
            // 4-2. 创建云渲染房间 从云渲染跳转 判断没有云渲染房间； 房间名为`${昵称}的云渲染专属房间`， ext"{type: 2}"
            if( this._ownerInfo.from === "xuanran" && !default_server?.xuanran){
                const res = await this._creatRoom(`${nickname}的云渲染专属房间`, `云渲染专属房间`, `${JSON.stringify({type: 2})}`, 2)
                if(res){
                    this._log(`后台创建“云渲染专属房间”成功${JSON.stringify(res)}`)
                    this._ownerInfo.hasRoomXuanran = true;

                    // 将房间插入到队首
                    this._rooms.unshift(res.data);
                    this._roomCreatedForXuanran.push(res.data)
                    this._roomForOwner.push(res.data)
                }
            }
        }

        // 5. 房间排序（所有分页）
        if(this._rooms?.length > 0 && serverId){
            this._rooms = await this._sort(this._rooms, serverId)
        }

        // 6. 房间重命名
        for (let i = 0, len = this._rooms.length; i < len; i++) {
            let ext;
            try {
                ext = JSON.parse(this._rooms[i].ext)
            }catch(err) {
                console.error(`ext不是json字符串:${err}`)
            }

            const isSelfRoom = Number(this._rooms[i].owner) === Number(user_id);
            if(ext?.type){
                const {type} = ext;
                if(Number(type) === 1){// 默认创建房间
                    if(!ext?.isEdit){// 未编辑过房间信息
                        if(isSelfRoom){
                            this._rooms[i].name = "我的房间"
                        }else {
                            this._rooms[i].name = `${nickname}的房间`;
                        }
                    }
                }
            }
        }

        // 7. 房间去重（处理跳转房间时，新增房间在队首，后续分页请求数据中会出现重复房间; 规则：重复房间中保留索引小的房间）
        this._rooms = this._removeDuplicates(this._rooms);

        // 8. 房间跳转
        // 默认创建房间不跳转
        if(this._roomCreatedForDef.length >= 1) {
            this._ownerInfo.hasRoomDefault = true;
            if(this._roomCreatedForDef.length > 1)  console.error(`${user_id}用户的默认创建房间不唯一`)
        }
        if(this._ownerInfo.from === "xuanran" && this._roomCreatedForXuanran.length >= 1) {
            this._ownerInfo.hasRoomXuanran = true;
            // 获取房间Id
            this._redirectServerId = this._roomCreatedForXuanran[0].id;
            // 获取改图区频道Id
            this._redirectChannelId = await this._getServerChannelDetail(this._redirectServerId)
            if(this._roomCreatedForXuanran.length > 1) console.error(`${user_id}用户的云渲染房间不唯一`)
        }

        // 9. 返回房间列表和创建&&跳转的房间ID
        console.log('dealRooms this...', this)
        this._log(`this====================,${JSON.stringify(this)}`);
        return {
            rooms: _.cloneDeep(this._rooms),
            redirectServerId: this._redirectServerId,
            redirectChannelId: this._redirectChannelId,
            extra: this._ownerInfo.extra,
            myRooms: this._roomForOwner
        }

    }

    /**
     * 当前进入房间置顶
     * @param list
     * @param currServerId
     * 用法：监听 current serverId, 回调该方法
     */
    async _sort(list, currServerId){
        // 1. 重排 （规则： this._room 中存在该房间，将房间放到队首；反之， 通过华信API获取房间信息, 放在队首）
        const index = list.findIndex((item) => item.id === currServerId);

        if(currServerId){
            if(index !== -1){
                // 1-1. id为“currServerId”的房间存在
                list = this._moveToFrontById(list, currServerId);
            }else {
                // 1-2. id为“currServerId”的房间不存在
                const res = await this._getRoomDetail(currServerId)

                const isSelfRoom = Number(res.data.owner) === Number(this._ownerInfo.user_id);
                if(res.data && isSelfRoom){
                    list.unshift(res.data);
                }
            }
        }else {
            this._log(`currServerId不存在`)
        }

        // 2. 滚动置顶
        const element = document.querySelector('.infinite-scroll-component__outerdiv');
        element.scrollTop = 0;

        return list;
    }
    async sort(list, currServerId) {

        if(list?.length > 0 && currServerId){
            const rooms = await this._sort(list, currServerId)
            return _.cloneDeep(rooms);
        }else {
            return false
        }
    }
}

export default new DealRooms();